/**
 * PIN 시스템 마이그레이션 배너
 * 사용자에게 새로운 단순화된 PIN 시스템으로 전환을 안내
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePin } from '../../contexts/PinContext';
import { Button } from '../UI';
import { 
  AlertCircle, 
  Shield, 
  ArrowRight, 
  Check, 
  X,
  Loader,
  Info
} from 'lucide-react';
import { 
  checkMigrationStatus, 
  migratePinSystem,
  validateMigration 
} from '../../utils/pinMigration';
import './PinMigrationBanner.css';

function PinMigrationBanner() {
  const { user } = useAuth();
  const { pinSettings, setMasterPin } = usePin();
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(null);
  const [migrationComplete, setMigrationComplete] = useState(false);
  const [error, setError] = useState(null);
  const [showBanner, setShowBanner] = useState(false);

  // 마이그레이션 상태 체크
  useEffect(() => {
    if (user) {
      checkForMigration();
    }
  }, [user]);

  const checkForMigration = async () => {
    setIsChecking(true);
    try {
      const status = await checkMigrationStatus(user.uid);
      setMigrationStatus(status);
      setShowBanner(status.needsMigration);
    } catch (err) {
      console.error('Error checking migration status:', err);
      setError('마이그레이션 상태 확인 실패');
    } finally {
      setIsChecking(false);
    }
  };

  // 마이그레이션 실행
  const handleMigration = async () => {
    if (!pinSettings.hasMasterPin) {
      setError('먼저 Master PIN을 설정해주세요.');
      return;
    }

    setIsMigrating(true);
    setError(null);
    
    try {
      const results = await migratePinSystem(user.uid, (progress) => {
        setMigrationProgress(progress);
      });

      if (results.success) {
        // 검증
        const validation = await validateMigration(user.uid);
        if (validation.isValid) {
          setMigrationComplete(true);
          setTimeout(() => {
            setShowBanner(false);
          }, 5000);
        } else {
          setError('마이그레이션 검증 실패. 일부 항목이 제대로 전환되지 않았습니다.');
        }
      } else {
        setError(`마이그레이션 중 ${results.errors.length}개 오류 발생`);
      }
    } catch (err) {
      console.error('Migration error:', err);
      setError('마이그레이션 실패: ' + err.message);
    } finally {
      setIsMigrating(false);
      setMigrationProgress(null);
    }
  };

  // 배너 닫기
  const handleDismiss = () => {
    if (!migrationStatus?.needsMigration || migrationComplete) {
      setShowBanner(false);
    }
  };

  // 로딩 중이거나 배너를 표시하지 않을 경우
  if (isChecking || !showBanner) {
    return null;
  }

  // 마이그레이션 완료
  if (migrationComplete) {
    return (
      <div className="pin-migration-banner pin-migration-success">
        <div className="pin-migration-icon">
          <Check size={24} />
        </div>
        <div className="pin-migration-content">
          <h3>PIN 시스템 업그레이드 완료!</h3>
          <p>이제 하나의 Master PIN으로 모든 보호된 항목에 접근할 수 있습니다.</p>
        </div>
        <button 
          onClick={handleDismiss} 
          className="pin-migration-close"
          aria-label="닫기"
        >
          <X size={20} />
        </button>
      </div>
    );
  }

  return (
    <div className="pin-migration-banner">
      <div className="pin-migration-icon">
        <Shield size={24} />
      </div>
      
      <div className="pin-migration-content">
        <h3>PIN 시스템이 개선되었습니다!</h3>
        
        {!pinSettings.hasMasterPin ? (
          <>
            <p>
              새로운 단순화된 PIN 시스템을 사용하려면 먼저 Master PIN을 설정해주세요.
            </p>
            <div className="pin-migration-info">
              <Info size={16} />
              <span>Master PIN 설정 후 기존 개별 PIN들을 자동으로 전환할 수 있습니다.</span>
            </div>
          </>
        ) : (
          <>
            <p>
              기존의 복잡한 개별 PIN 시스템을 하나의 Master PIN으로 통합할 수 있습니다.
            </p>
            
            {migrationStatus && (
              <div className="pin-migration-stats">
                <div className="stat-item">
                  <strong>{migrationStatus.notesWithIndividualPins}</strong>
                  <span>개의 메모</span>
                </div>
                <div className="stat-item">
                  <strong>{migrationStatus.filesWithIndividualPins}</strong>
                  <span>개의 파일</span>
                </div>
                <div className="stat-item">
                  <ArrowRight size={16} />
                </div>
                <div className="stat-item highlight">
                  <strong>1개</strong>
                  <span>Master PIN</span>
                </div>
              </div>
            )}

            <div className="pin-migration-benefits">
              <h4>개선 사항:</h4>
              <ul>
                <li><Check size={14} /> 하나의 PIN으로 모든 항목 보호</li>
                <li><Check size={14} /> 더 간단하고 직관적인 사용</li>
                <li><Check size={14} /> 향상된 보안과 성능</li>
              </ul>
            </div>
          </>
        )}

        {error && (
          <div className="pin-migration-error">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {isMigrating && migrationProgress && (
          <div className="pin-migration-progress">
            <Loader className="spin" size={16} />
            <span>{migrationProgress.message}</span>
          </div>
        )}
      </div>

      <div className="pin-migration-actions">
        {!pinSettings.hasMasterPin ? (
          <Button
            variant="primary"
            onClick={() => {
              // Settings 페이지의 PIN 설정 섹션으로 이동
              window.location.href = '/settings#pin-management';
            }}
          >
            Master PIN 설정하기
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleMigration}
            disabled={isMigrating}
          >
            {isMigrating ? '전환 중...' : '지금 전환하기'}
          </Button>
        )}
        
        {!isMigrating && (
          <button 
            onClick={handleDismiss} 
            className="pin-migration-dismiss"
          >
            나중에
          </button>
        )}
      </div>
    </div>
  );
}

export default PinMigrationBanner;